import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, ChevronDown, X, ChevronRight } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { subcategories } from '../data/data';
import { useStore } from '../admin/context/ProductStore';
import './Shop.css';

const sortOptions = [
  { value: 'popular',    label: 'Most Popular' },
  { value: 'price-asc',  label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },

  { value: 'discount',   label: 'Best Discount' },
];

export default function Shop({ onAddToCart }) {
  const { products = [], categories = [], loading } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort,         setSort]         = useState('popular');
  const [maxPrice,     setMaxPrice]     = useState(2000);
  const [filterOpen,   setFilterOpen]   = useState(false);
  const [expandedCats, setExpandedCats] = useState({});

  // Only show published products on storefront
  const publishedProducts = products.filter(p => p.status === 'published');

  const activeCat  = searchParams.get('cat')    || 'all';
  const activeSub  = searchParams.get('sub')    || '';
  const searchQuery = searchParams.get('search') || '';

  const filtered = useMemo(() => {
    let res = [...publishedProducts];
    if (activeCat !== 'all') res = res.filter(p => p.category === activeCat);
    if (activeSub)           res = res.filter(p => p.subcat   === activeSub);
    if (searchQuery)         res = res.filter(p =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    res = res.filter(p => p.price <= maxPrice);
    if (sort === 'price-asc')  res.sort((a,b) => a.price - b.price);
    if (sort === 'price-desc') res.sort((a,b) => b.price - a.price);

    if (sort === 'discount') {
      res.sort((a,b) => {
        const discA = a.mrp > 0 ? (a.mrp - a.price) / a.mrp : 0;
        const discB = b.mrp > 0 ? (b.mrp - b.price) / b.mrp : 0;
        return discB - discA;
      });
    }
    return res;
  }, [publishedProducts, activeCat, activeSub, searchQuery, sort, maxPrice]);

  const setCategory = (slug, sub = '') => {
    if (slug === 'all') { setSearchParams({}); }
    else if (sub)       { setSearchParams({ cat: slug, sub }); }
    else                { setSearchParams({ cat: slug }); }
  };

  const toggleCat = (id) => setExpandedCats(prev => ({ ...prev, [id]: !prev[id] }));

  const activeCatObj = categories.find(c => c.slug === activeCat);
  const pageTitle = searchQuery
    ? `Results for "${searchQuery}"`
    : activeSub
      ? (subcategories[activeCat] || []).find(s => s.id === activeSub)?.name || activeCatObj?.name
      : activeCatObj?.name || 'All Products';

  return (
    <main className="shop-page">
      {/* Page Hero */}
      <div className="shop-hero">
        <div className="container">
          <span className="eyebrow" style={{ color: 'var(--yellow)' }}>Our Products</span>
          <h1 className="shop-title">{pageTitle}</h1>
          <p className="shop-subtitle">{filtered.length} products available</p>
        </div>
      </div>

      <div className="container shop-layout">
        {/* ── SIDEBAR ── */}
        <aside className={`shop-sidebar ${filterOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h3>Filters</h3>
            <button className="sidebar-close action-btn" onClick={() => setFilterOpen(false)}>
              <X size={18} />
            </button>
          </div>

          {/* All Products */}
          <div className="filter-section">
            <button
              className={`filter-option ${activeCat === 'all' ? 'active' : ''}`}
              onClick={() => { setCategory('all'); setFilterOpen(false); }}
            >
              <span>🛒 All Products</span>
              <span className="filter-count">{publishedProducts.length}</span>
            </button>
          </div>

          {/* Category + Subcategory accordion */}
          <div className="filter-section">
            <h4 className="filter-title">Categories</h4>
            {categories.map(cat => {
              const subs    = subcategories[cat.id] || [];
              const catProducts = publishedProducts.filter(p => p.category === cat.id);
              const isActive = activeCat === cat.slug;
              const isOpen   = expandedCats[cat.id] || isActive;
              return (
                <div key={cat.id} className="cat-accordion">
                  <button
                    className={`filter-option cat-parent ${isActive ? 'active' : ''}`}
                    onClick={() => { setCategory(cat.slug); toggleCat(cat.id); setFilterOpen(false); }}
                  >
                    <span className="cat-opt-inner">
                      <span>{cat.emoji}</span>
                      <span>{cat.name}</span>
                    </span>
                    <span className="cat-opt-right">
                      <span className="filter-count">{catProducts.length}</span>
                      {subs.length > 0 && (
                        <button
                          className="expand-btn"
                          onClick={e => { e.stopPropagation(); toggleCat(cat.id); }}
                        >
                          <ChevronRight size={13} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: '0.2s' }} />
                        </button>
                      )}
                    </span>
                  </button>
                  {isOpen && subs.length > 0 && (
                    <div className="sub-list">
                      {subs.map(sub => (
                        <button
                          key={sub.id}
                          className={`sub-option ${activeSub === sub.id ? 'active' : ''}`}
                          onClick={() => { setCategory(cat.slug, sub.id); setFilterOpen(false); }}
                        >
                          <span className="sub-dot" />
                          {sub.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Price range */}
          <div className="filter-section">
            <h4 className="filter-title">Max Price</h4>
            <div className="price-labels">
              <span>₹0</span>
              <span className="price-val">₹{maxPrice}</span>
            </div>
            <input
              type="range" min={100} max={2000} step={50}
              value={maxPrice}
              onChange={e => setMaxPrice(Number(e.target.value))}
              className="range-input"
            />
          </div>

          {/* Quick filters */}
          <div className="filter-section">
            <h4 className="filter-title">Quick Pick</h4>
            <div className="quick-filters">
              {['Best Seller','Premium','New','Superfood','Popular','Gift'].map(tag => (
                <button key={tag} className="quick-filter-chip">{tag}</button>
              ))}
            </div>
          </div>
        </aside>

        {/* ── MAIN ── */}
          <div className="shop-main" style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.5s ease' }}>
          {/* Breadcrumb pills */}
          {(activeCat !== 'all' || activeSub) && (
            <div className="active-filters">
              {activeCat !== 'all' && (
                <span className="active-filter-chip">
                  {activeCatObj?.emoji} {activeCatObj?.name}
                  <button onClick={() => setCategory('all')}><X size={12} /></button>
                </span>
              )}
              {activeSub && (
                <span className="active-filter-chip">
                  {(subcategories[activeCat] || []).find(s => s.id === activeSub)?.name}
                  <button onClick={() => setCategory(activeCat)}><X size={12} /></button>
                </span>
              )}
            </div>
          )}

          {/* Toolbar */}
          <div className="shop-toolbar">
            <button className="filter-toggle-btn" onClick={() => setFilterOpen(true)}>
              <SlidersHorizontal size={16} /> Filters
            </button>
            <span className="result-count">{filtered.length} products</span>
            <div className="sort-select-wrap">
              <select className="sort-select" value={sort} onChange={e => setSort(e.target.value)}>
                {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <ChevronDown size={14} className="sort-chevron" />
            </div>
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="no-results">
              <p className="no-results-emoji">🔍</p>
              <h3>No products found</h3>
              <p>Try a different category, subcategory, or search term.</p>
              <button className="btn-primary" onClick={() => setSearchParams({})}>
                View All Products
              </button>
            </div>
          ) : (
            <div className="shop-grid grid-4">
              {filtered.map(p => (
                <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />
              ))}
            </div>
          )}
        </div>
      </div>

      {filterOpen && <div className="sidebar-overlay" onClick={() => setFilterOpen(false)} />}
    </main>
  );
}
